import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer, { MobileContactBar } from "@/components/Footer";
import Home from "@/pages/Home";
import Asphalt from "@/pages/Asphalt";
import Maintenance from "@/pages/Maintenance";
import ConstructionPage from "@/pages/Construction";
import Careers from "@/pages/Careers";
import News from "@/pages/News";
import OurWork from "@/pages/OurWork";
import AboutPage from "@/pages/AboutPage";
import Contact from "@/pages/Contact";
import QuotePage from "@/pages/QuotePage";
import Admin from "@/pages/Admin";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-joell-black px-6 text-center">
          <p className="font-display text-3xl font-bold uppercase">Something went wrong</p>
          <button className="btn-accent" onClick={() => window.location.reload()}>
            Reload site
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
}

function SiteChrome({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-joell-black">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <MobileContactBar />
      {/* spacer so the fixed mobile bar never covers footer content */}
      <div className="h-14 bg-joell-ink sm:hidden" aria-hidden="true" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route path="/" element={<SiteChrome><Home /></SiteChrome>} />
          <Route path="/asphalt-services" element={<SiteChrome><Asphalt /></SiteChrome>} />
          <Route path="/maintenance" element={<SiteChrome><Maintenance /></SiteChrome>} />
          <Route path="/construction-services" element={<SiteChrome><ConstructionPage /></SiteChrome>} />
          <Route path="/careers" element={<SiteChrome><Careers /></SiteChrome>} />
          <Route path="/news" element={<SiteChrome><News /></SiteChrome>} />
          <Route path="/home-maintenance" element={<SiteChrome><Navigate to="/maintenance" replace /></SiteChrome>} />
          <Route path="/our-work" element={<SiteChrome><OurWork /></SiteChrome>} />
          <Route path="/about" element={<SiteChrome><AboutPage /></SiteChrome>} />
          <Route path="/contact" element={<SiteChrome><Contact /></SiteChrome>} />
          <Route path="/request-quote" element={<SiteChrome><QuotePage /></SiteChrome>} />
          <Route path="/quote" element={<SiteChrome><QuotePage /></SiteChrome>} />
          <Route path="*" element={<SiteChrome><Home /></SiteChrome>} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
